import { Stack, Text, Textarea } from "@mantine/core";

import { useFeedbackDraftStore } from "../../store";
import classes from "../../styles.module.css";
import { type FreeTextCategory, feedbackUIMappingFreeText, getTemplateFields } from "../../types";
import { FeedbackRadioGroup } from "./FeedbackRadioGroup";

function getPlaceholder(category?: string) {
    const freeTextCategory = category as FreeTextCategory | undefined;
    const { placeholder } = feedbackUIMappingFreeText.default;
    return freeTextCategory ? placeholder[freeTextCategory] : placeholder["Make a suggestion"];
}

export function FreeTextFeedback({ isLoading, isTraining }: { isLoading: boolean; isTraining?: boolean }) {
    const { draft, setDraft } = useFeedbackDraftStore();
    const { subtitle, categories, bottomText } = isTraining
        ? feedbackUIMappingFreeText.expertUniversityDefault
        : feedbackUIMappingFreeText.default;

    const templateFields = getTemplateFields(draft.category);

    const onChangeText = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDraft({
            ...draft,
            description: event.currentTarget.value,
        });
    };

    const onChangeTemplateField = (field: string, value: string) => {
        setDraft({
            ...draft,
            templateResponses: {
                ...draft.templateResponses,
                [field]: value,
            },
        });
    };

    return (
        <Stack>
            <Text size="sm" c="dark.3" mb="lg">
                {subtitle}
            </Text>
            <FeedbackRadioGroup categories={categories} direction="row" />
            {templateFields ? (
                <Stack mt="lg" gap="md">
                    {templateFields.map((field) => (
                        <Stack key={field} gap="xxs">
                            <Text size="sm" fw={600}>
                                {field}
                            </Text>
                            <Textarea
                                classNames={{ input: classes.input }}
                                value={draft.templateResponses?.[field] ?? ""}
                                minRows={2}
                                maxRows={4}
                                autosize
                                onChange={(e) => onChangeTemplateField(field, e.currentTarget.value)}
                                data-testid={`feedback-template-field-${field}`}
                                disabled={isLoading}
                            />
                        </Stack>
                    ))}
                </Stack>
            ) : (
                <Textarea
                    classNames={{ input: classes.input }}
                    value={draft.description}
                    minRows={12}
                    maxRows={12}
                    mt="lg"
                    autosize
                    onChange={onChangeText}
                    placeholder={getPlaceholder(draft.category)}
                    data-testid="feedback-textarea"
                    disabled={isLoading}
                />
            )}
            <Text size="xs" c="dark.3" mt="xs" mb="xs">
                {bottomText}
            </Text>
        </Stack>
    );
}
